import {useState, useCallback} from 'react'
import {useSearchParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {authClient} from '../auth-client'
import {AuthLayout, AuthLink, ErrorBanner, fieldClass, primaryButtonClass} from './auth-layout'

export function ResetPasswordPage() {
  const {t} = useTranslation()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? undefined
  const tokenError = params.get('error')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()

    if (password !== confirm) {
      setError(t('auth.resetPassword.errorMismatch'))
      return
    }

    if (!token) {
      setError(t('auth.resetPassword.errorInvalidToken'))
      return
    }

    setSubmitting(true)
    setError(undefined)

    const {error} = await authClient.resetPassword({newPassword: password, token})

    setSubmitting(false)

    if (error) {
      setError(t('auth.resetPassword.errorInvalidToken'))
      return
    }

    void navigate('/sign-in')
  }, [password, confirm, token, navigate, t])

  if (tokenError || !token) {
    return (
      <AuthLayout title={t('auth.resetPassword.invalidTitle')} footer={<AuthLink to='/forgot-password'>{t('auth.resetPassword.requestNewLink')}</AuthLink>}>
        <ErrorBanner message={t('auth.resetPassword.invalidMessage')} />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t('auth.resetPassword.title')} footer={<AuthLink to='/sign-in'>{t('auth.resetPassword.backToSignIn')}</AuthLink>}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        {error && <ErrorBanner message={error} />}
        <input
          type='password' placeholder={t('auth.fields.newPassword')} value={password}
          required minLength={8} autoFocus
          autoComplete='new-password' className={fieldClass}
          onChange={e => {
            setPassword(e.target.value)
          }}
        />
        <input
          type='password' placeholder={t('auth.fields.confirmPassword')} value={confirm}
          required minLength={8}
          autoComplete='new-password' className={fieldClass}
          onChange={e => {
            setConfirm(e.target.value)
          }}
        />
        <button type='submit' disabled={submitting} className={primaryButtonClass}>
          {submitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
        </button>
      </form>
    </AuthLayout>
  )
}
